/**
 * Vacant Property Security — Ring Alarm command center.
 *
 * One screen showing every unit currently flagged vacant_unsecured or
 * vacant_alarmed in the tenant, plus:
 *  - alarm status (installed y/n, sensor/camera count, when it was armed)
 *  - last-checked timestamp + who
 *  - event log (checks, false alarms, intrusions)
 *  - buttons: log check, edit alarm details, auto-create Ring install
 *    ProjectRadar project.
 *
 * Admins/founder can mutate. Workers (Procall staff) get read-only + event
 * logging (they're the ones physically doing the drive-by checks).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck, ShieldWarning, House, Clock, User,
  Camera, Bell, WarningCircle, CheckCircle, Plus, X,
  Siren, Truck, Fire,
} from "@phosphor-icons/react";

const EVENT_META = {
  check:          { label: "Checked",       icon: CheckCircle,   cls: "bg-emerald-100 text-emerald-800 border-emerald-500" },
  false_alarm:    { label: "False alarm",   icon: Bell,          cls: "bg-amber-100 text-amber-900 border-amber-500" },
  intrusion:      { label: "Intrusion!",    icon: WarningCircle, cls: "bg-red-100 text-red-900 border-red-600" },
  sensor_offline: { label: "Sensor offline",icon: WarningCircle, cls: "bg-orange-100 text-orange-900 border-orange-500" },
  other:          { label: "Other",         icon: Bell,          cls: "bg-slate-100 text-slate-700 border-slate-400" },
};

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / 86400000);
}

function StatusPill({ status }) {
  if (status === "vacant_alarmed") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-emerald-600 text-white" data-testid="pill-alarmed">
        <ShieldCheck weight="fill" size={12}/> Alarmed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white animate-pulse" data-testid="pill-unsecured">
      <ShieldWarning weight="fill" size={12}/> Unsecured
    </span>
  );
}

function EventRow({ ev }) {
  const meta = EVENT_META[ev.type] || EVENT_META.other;
  const Icon = meta.icon;
  return (
    <div className={`flex items-start gap-2 p-2 border-l-4 ${meta.cls}`} data-testid={`event-${ev.id}`}>
      <Icon weight="fill" size={16} className="flex-shrink-0 mt-0.5"/>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-black uppercase tracking-widest">{meta.label}</div>
        <div className="text-xs text-slate-700">{ev.note || "—"}</div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
          {ev.by || "?"} · {(ev.at || "").slice(0, 16).replace("T", " ")}
        </div>
      </div>
    </div>
  );
}

function LogCheckModal({ property, onClose, onLogged }) {
  const [type, setType] = useState("check");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/property-configs/${property.id}/ring-alarm/events`, { type, note });
      toast.success("Logged");
      onLogged?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Couldn't log"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="log-event-modal" onClick={() => !busy && onClose()}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#dc2626] max-w-md w-full">
        <div className="p-4 border-b-4 border-[#0f172a] flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">Log Alarm Event</div>
          <button onClick={onClose} data-testid="log-close" className="text-slate-500"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Property</div>
            <div className="text-sm font-black">{property.address || property.address_key} · Unit {property.unit}</div>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Event type</div>
            <div className="grid grid-cols-2 gap-1">
              {Object.keys(EVENT_META).map(k => (
                <button key={k} onClick={() => setType(k)} data-testid={`log-type-${k}`}
                  className={`px-3 py-2 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest ${type === k ? "bg-[#0f172a] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {EVENT_META[k].label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Note (optional)</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} data-testid="log-note"
              className="w-full border-2 border-[#0f172a] p-2 text-sm"
              placeholder="e.g. Front door sensor tripped by wind — reset. All quiet."/>
          </div>
        </div>
        <div className="p-4 border-t-4 border-[#0f172a] flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="h-10 px-4 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="log-submit"
            className="h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
            {busy ? "Logging…" : "Log Event"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlarmDetailsModal({ property, onClose, onSaved }) {
  const r = property.ring_alarm || {};
  const [form, setForm] = useState({
    installed_at: r.installed_at || "",
    installed_by: r.installed_by || "",
    ring_account_email: r.ring_account_email || "",
    base_station_serial: r.base_station_serial || "",
    sensor_count: r.sensor_count ?? 0,
    camera_count: r.camera_count ?? 0,
    app_shared_with: (r.app_shared_with || []).join(", "),
    site_code: r.site_code || "",
    lifecycle_status: r.lifecycle_status || "active",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/property-configs/${property.id}/ring-alarm`, {
        ...form,
        sensor_count: parseInt(form.sensor_count) || 0,
        camera_count: parseInt(form.camera_count) || 0,
        app_shared_with: form.app_shared_with.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Alarm details saved");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const decommission = async (status) => {
    const labels = { stolen: "Mark STOLEN", returned: "Mark RETURNED to Ring", retired: "Mark RETIRED (we erased it)", transferred: "Mark TRANSFERRED" };
    const note = window.prompt(`${labels[status]} — add a note (optional):`, "") ?? null;
    if (note === null) return;
    const erase = status === "retired" && window.confirm("Erase hardware data too? (clears base station serial + sensor/camera counts but keeps history)");
    setBusy(true);
    try {
      await api.post(`/property-configs/${property.id}/ring-alarm/lifecycle`, {
        status, note, erase_data: !!erase,
      });
      toast.success(labels[status].replace("Mark ", ""));
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Couldn't update"); }
    finally { setBusy(false); }
  };
  const field = (k, label, extra = {}) => (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input value={form[k]} onChange={e => setForm(f => ({...f, [k]: e.target.value}))}
        data-testid={`alarm-field-${k}`}
        className="w-full h-10 border-2 border-[#0f172a] px-2 text-sm" {...extra}/>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="alarm-details-modal" onClick={() => !busy && onClose()}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#10b981] max-w-lg w-full max-h-[92vh] overflow-auto">
        <div className="p-4 border-b-4 border-[#0f172a] flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">🔔 Ring Alarm Details</div>
          <button onClick={onClose} className="text-slate-500"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-600">
            {property.address || property.address_key} · Unit {property.unit}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {field("site_code", "Site code (e.g. ST001)", { placeholder: "ST001" })}
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Lifecycle status</div>
              <select value={form.lifecycle_status}
                onChange={e => setForm(f => ({...f, lifecycle_status: e.target.value}))}
                data-testid="alarm-field-lifecycle_status"
                className="w-full h-10 border-2 border-[#0f172a] px-2 text-sm">
                <option value="active">Active — in service</option>
                <option value="stolen">Stolen</option>
                <option value="returned">Returned to Ring</option>
                <option value="retired">Retired / erased</option>
                <option value="transferred">Transferred</option>
              </select>
            </div>
          </div>
          {field("installed_at", "Installed on (YYYY-MM-DD)", { placeholder: "2026-02-15" })}
          {field("installed_by", "Installed by")}
          {field("ring_account_email", "Ring account email", { type: "email" })}
          {field("base_station_serial", "Base station serial")}
          <div className="grid grid-cols-2 gap-2">
            {field("sensor_count", "Sensors installed", { type: "number", min: 0 })}
            {field("camera_count", "Cameras installed", { type: "number", min: 0 })}
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              App shared with (comma-separated emails)
            </div>
            <textarea rows={2} value={form.app_shared_with} data-testid="alarm-field-app_shared_with"
              onChange={e => setForm(f => ({...f, app_shared_with: e.target.value}))}
              className="w-full border-2 border-[#0f172a] p-2 text-sm"
              placeholder="andrew@gopmr.com, procall@gopmr.com"/>
          </div>
          {/* Decommission actions */}
          <div className="border-t-2 border-slate-200 pt-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
              Decommission / Reprogram
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <button onClick={() => decommission("stolen")} disabled={busy} data-testid="alarm-mark-stolen"
                className="h-9 px-2 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                Mark Stolen
              </button>
              <button onClick={() => decommission("returned")} disabled={busy} data-testid="alarm-mark-returned"
                className="h-9 px-2 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                Returned to Ring
              </button>
              <button onClick={() => decommission("retired")} disabled={busy} data-testid="alarm-mark-retired"
                className="h-9 px-2 bg-slate-700 hover:bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                Retired / Erased
              </button>
              <button onClick={() => decommission("transferred")} disabled={busy} data-testid="alarm-mark-transferred"
                className="h-9 px-2 bg-sky-700 hover:bg-sky-800 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                Transferred
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 border-t-4 border-[#0f172a] flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="h-10 px-4 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="alarm-save"
            className="h-10 px-4 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
            {busy ? "Saving…" : "Save Alarm Details"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Bulk Add — paste a list from Ring's Locations screen and we fuzzy-match
// each line to a property_configs row. User can review the match report
// before committing.
function BulkAddAlarmsModal({ onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const parseLines = () => {
    const out = [];
    for (const raw of text.split(/\n+/)) {
      const line = raw.trim();
      if (!line) continue;
      // First token = site code, rest = address hint. Try "SITE address"
      // and fallback to "SITE - address".
      const m = line.match(/^([A-Za-z0-9\-]+)\s+[\-\–]?\s*(.+)$/);
      if (!m) continue;
      const site_code = m[1].trim();
      let hint = m[2].trim();
      // Pull a unit if the line says "Unit 1" or "Un 1"
      let unit = null;
      const uMatch = hint.match(/\b(?:unit|un)\s+(\S+)\b/i);
      if (uMatch) { unit = uMatch[1]; hint = hint.replace(uMatch[0], "").trim(); }
      out.push({ site_code, address_hint: hint, unit });
    }
    return out;
  };
  const run = async (dry_run) => {
    const lines = parseLines();
    if (!lines.length) { toast.error("Paste at least one line"); return; }
    setBusy(true);
    try {
      const r = await api.post("/property-configs/ring-alarm/bulk-add",
        { lines, dry_run });
      setReport(r.data);
      if (!dry_run) {
        toast.success(`Attached ${r.data.applied} alarm${r.data.applied === 1 ? "" : "s"}`);
        onSaved?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Bulk add failed");
    } finally { setBusy(false); }
  };
  const toneByStatus = {
    applied: "bg-emerald-100 text-emerald-900 border-emerald-500",
    would_apply: "bg-cyan-100 text-cyan-900 border-cyan-500",
    ambiguous: "bg-amber-100 text-amber-900 border-amber-500",
    no_match: "bg-red-100 text-red-900 border-red-500",
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="bulk-add-alarms-modal" onClick={() => !busy && onClose()}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#10b981] max-w-2xl w-full max-h-[92vh] overflow-auto">
        <div className="p-4 border-b-4 border-[#0f172a] flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">📍 Bulk Add Alarms</div>
          <button onClick={onClose} className="text-slate-500"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-600">
            Paste Ring's Locations list (one per line). We fuzzy-match each address
            against your properties and attach the site code.
          </div>
          <textarea rows={10} value={text} onChange={e => setText(e.target.value)}
            data-testid="bulk-add-textarea"
            placeholder={"ST001 5028 N 12th St\nRH003 4122 Ogden Unit 1\nPC009 1346 S 30th St"}
            className="w-full border-2 border-[#0f172a] p-2 text-sm font-mono"/>
          <div className="flex gap-2">
            <button onClick={() => run(true)} disabled={busy || !text.trim()}
              data-testid="bulk-add-preview-btn"
              className="h-10 px-4 border-2 border-[#0f172a] text-slate-900 bg-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
              Preview matches
            </button>
            <button onClick={() => run(false)} disabled={busy || !text.trim()}
              data-testid="bulk-add-apply-btn"
              className="h-10 px-4 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
              {busy ? "Working…" : "Apply all unambiguous"}
            </button>
          </div>
          {report && (
            <div className="border-t-2 border-slate-200 pt-3 space-y-1" data-testid="bulk-add-report">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {report.applied}/{report.total} attached · dry_run={String(report.dry_run)}
              </div>
              {(report.report || []).map((r, i) => (
                <div key={i}
                  className={`text-xs p-1.5 border-l-4 ${toneByStatus[r.status] || "bg-slate-100 border-slate-400"}`}>
                  <b className="font-mono">{r.site_code}</b> · {r.address_hint}
                  <span className="ml-2 text-[10px] font-black uppercase tracking-widest opacity-80">{r.status}</span>
                  {r.address && <div className="text-[10px] text-slate-600 mt-0.5">→ {r.address}</div>}
                  {r.candidates?.length > 1 && <div className="text-[10px] text-amber-700 mt-0.5">⚠️ {r.candidates.length} candidate units — pick manually</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PropertyCard({ p, readOnly, onLog, onEdit, onEditVacancy, onCreateProject, onFlip, onSendAddendum, sendingAddendum }) {
  const r = p.ring_alarm || {};
  const lastCheck = daysSince(r.last_checked_at);
  const stale = lastCheck === null || lastCheck > 7;
  return (
    <div className="bg-white border-2 border-[#0f172a] shadow-[4px_4px_0px_#0f172a]" data-testid={`vacant-card-${p.id}`}>
      <div className="p-3 border-b-2 border-slate-100 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={p.vacancy_status}/>
            {p.went_vacant_at && (
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                {daysSince(p.went_vacant_at)}d vacant
              </span>
            )}
          </div>
          <div className="font-display font-black text-base uppercase tracking-tight mt-1 truncate">
            {p.address || p.address_key || "—"}
          </div>
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
            Unit {p.unit || "—"}
          </div>
        </div>
        <div className="text-right">
          {r.sensor_count ? (
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
              🔒 {r.sensor_count} sensor{r.sensor_count === 1 ? "" : "s"}
            </div>
          ) : null}
          {r.camera_count ? (
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">
              📷 {r.camera_count} cam{r.camera_count === 1 ? "" : "s"}
            </div>
          ) : null}
          {(p.deployed_cameras || []).length > 0 && (
            <div className="mt-1 text-[9px] font-mono font-black text-indigo-700 flex flex-wrap gap-1 justify-end max-w-[200px]"
              data-testid={`deployed-cams-${p.id}`}>
              {(p.deployed_cameras || []).map(c => (
                <span key={c.system_code} title={`${c.owner} · last moved ${c.last_deployed_at?.slice(0,10) || 'never'}`}
                  className={`px-1.5 py-0.5 border ${c.owner === "raza" ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}>
                  {c.system_code}
                </span>
              ))}
            </div>
          )}
          {p.alarm_addendum_sent_at && (
            <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-500"
              title={`Sent to ${p.alarm_addendum_sent_to || 'landlord'}`}>
              📄 Addendum sent
            </div>
          )}
        </div>
      </div>

      <div className={`p-3 grid grid-cols-2 gap-3 text-[11px] ${stale && p.vacancy_status === "vacant_alarmed" ? "bg-amber-50" : ""}`}>
        <div>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Last check</div>
          <div className={`text-sm font-black ${stale && p.vacancy_status === "vacant_alarmed" ? "text-amber-800" : "text-slate-800"}`}>
            {r.last_checked_at ? `${lastCheck}d ago` : "Never"}
          </div>
          {r.last_checked_by && <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{r.last_checked_by}</div>}
        </div>
        <div>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Ring account</div>
          <div className="text-sm font-black text-slate-800 truncate">{r.ring_account_email || "—"}</div>
        </div>
      </div>

      {(r.events || []).length > 0 && (
        <div className="border-t-2 border-slate-100">
          <details className="group">
            <summary className="p-2 flex items-center gap-2 cursor-pointer list-none">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 group-open:text-slate-800">
                📋 Event log · {r.events.length}
              </span>
              <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-400 group-open:hidden">tap to open</span>
            </summary>
            <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
              {r.events.slice(0, 25).map(ev => <EventRow key={ev.id} ev={ev}/>)}
            </div>
          </details>
        </div>
      )}

      <div className="p-2 border-t-2 border-slate-100 flex gap-1 flex-wrap">
        <button onClick={() => onLog(p)} data-testid={`btn-log-${p.id}`}
          className="h-8 px-2 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
          <Plus weight="bold" size={11}/> Log Check
        </button>
        {!readOnly && (
          <>
            <button onClick={() => onEditVacancy(p)} data-testid={`btn-edit-vacancy-${p.id}`}
              className="h-8 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1"
              title="Edit address, unit, vacancy date, status">
              ✏️ Edit
            </button>
            <button onClick={() => onEdit(p)} data-testid={`btn-edit-${p.id}`}
              className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
              🔔 Alarm Details
            </button>
            {p.ring_install_project_id ? (
              <Link to={`/admin/projects/${p.ring_install_project_id}`}
                data-testid={`btn-open-project-${p.id}`}
                className="h-8 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
                🏗️ Open Install Project
              </Link>
            ) : (
              <button onClick={() => onCreateProject(p)} data-testid={`btn-create-project-${p.id}`}
                className="h-8 px-2 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
                🏗️ Create Install Project
              </button>
            )}
            <button onClick={() => onFlip(p)} data-testid={`btn-flip-${p.id}`}
              className={`h-8 px-2 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 ${p.vacancy_status === "vacant_alarmed" ? "bg-slate-200 hover:bg-slate-300 text-slate-700" : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}>
              {p.vacancy_status === "vacant_alarmed" ? "↺ Mark Unsecured" : "✓ Mark Alarmed"}
            </button>
            <button onClick={() => onSendAddendum(p)} data-testid={`btn-send-addendum-${p.id}`}
              disabled={sendingAddendum === p.id}
              className={`h-8 px-2 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 disabled:opacity-50 ${p.alarm_addendum_sent_at ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-2 border-slate-300" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
              title={p.alarm_addendum_sent_at ? `Last sent ${p.alarm_addendum_sent_at.slice(0,10)}` : "Email the landlord the signed alarm addendum PDF"}>
              📄 {sendingAddendum === p.id ? "Sending…" : (p.alarm_addendum_sent_at ? "Re-send Addendum" : "Send Landlord")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One-row vacancy intake — admin types address + unit, optionally pastes
 * the Ring site code, picks unsecured vs alarmed. Dupes by address+unit
 * silently update instead of forking a new config.
 *
 * Also used for EDIT when `editing` prop is a vacancy row (prefills fields;
 * save calls the same endpoint which dedupes by address+unit → update path).
 */
function AddVacantModal({ onClose, onSaved, editing = null, initial = null }) {
  const [address, setAddress] = useState(editing?.address || initial?.address || "");
  const [unit, setUnit] = useState(editing?.unit || initial?.unit || "");
  const [status, setStatus] = useState(
    editing?.vacancy_status || initial?.vacancy_status || "vacant_unsecured",
  );
  // Pre-fill went_vacant_at as YYYY-MM-DD so the native date picker lights up.
  const [wentVacantAt, setWentVacantAt] = useState(
    (editing?.went_vacant_at || initial?.went_vacant_at || "").slice(0, 10),
  );
  const [alarmCode, setAlarmCode] = useState(editing?.alarm_code || "");
  const [notes, setNotes] = useState(editing?.vacancy_notes || "");
  const [busy, setBusy] = useState(false);

  const isEdit = !!editing;

  const save = async () => {
    if (!address.trim()) return toast.error("Address is required");
    setBusy(true);
    try {
      const r = await api.post("/vacant-security/add-vacant", {
        address: address.trim(),
        unit: unit.trim(),
        vacancy_status: status,
        // Backend accepts ISO date; empty string means "today".
        went_vacant_at: wentVacantAt
          ? new Date(wentVacantAt + "T12:00:00Z").toISOString()
          : "",
        alarm_code: alarmCode.trim(),
        notes: notes.trim(),
      });
      toast.success(
        r.data?.created
          ? `✓ Added · ${r.data.address}${r.data.unit ? " · Unit " + r.data.unit : ""}`
          : `✓ Updated · ${r.data.address}${r.data.unit ? " · Unit " + r.data.unit : ""}`,
      );
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0f172a] shadow-[6px_6px_0_#000] max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-vacant-modal">
        <div className="flex items-center justify-between p-3 bg-[#0f172a] text-white">
          <div className="font-display font-black uppercase tracking-tight">
            {isEdit ? "Edit Vacant Unit" : "+ Add Vacant Unit"}
          </div>
          <button onClick={onClose} className="hover:bg-white/10 p-1">
            <X weight="bold" size={14}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Address *</label>
            <input value={address} onChange={e => setAddress(e.target.value)}
              placeholder="4122 Ogdon St"
              disabled={isEdit}
              data-testid="add-vacant-address"
              className="h-10 w-full border-2 border-[#0f172a] px-2 text-sm font-bold disabled:bg-slate-100 disabled:text-slate-500"/>
            {isEdit && (
              <p className="text-[10px] text-slate-500 italic mt-1">Address can't be changed on edit — it's the dedupe key.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Unit</label>
              <input value={unit} onChange={e => setUnit(e.target.value)}
                placeholder="2F"
                disabled={isEdit}
                data-testid="add-vacant-unit"
                className="h-10 w-full border-2 border-[#0f172a] px-2 text-sm font-bold disabled:bg-slate-100 disabled:text-slate-500"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                data-testid="add-vacant-status"
                className="h-10 w-full border-2 border-[#0f172a] px-2 text-sm font-bold bg-white">
                <option value="vacant_unsecured">Vacant · Unsecured</option>
                <option value="vacant_alarmed">Vacant · Alarmed</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Day it went vacant</label>
            <input type="date" value={wentVacantAt}
              onChange={e => setWentVacantAt(e.target.value)}
              data-testid="add-vacant-went-vacant-at"
              className="h-10 w-full border-2 border-[#0f172a] px-2 text-sm font-bold"/>
            <p className="text-[10px] text-slate-500 italic mt-1">
              Leave blank for today. Backdate so the "days vacant" counter is accurate from day one.
            </p>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Ring Site Code (optional)</label>
            <input value={alarmCode} onChange={e => setAlarmCode(e.target.value)}
              placeholder="ST001"
              data-testid="add-vacant-alarm-code"
              className="h-10 w-full border-2 border-[#0f172a] px-2 text-sm font-mono font-bold"/>
            <p className="text-[10px] text-slate-500 italic mt-1">Fill if a Ring alarm is already deployed — flips the status to Alarmed automatically.</p>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. keys picked up Oct 3, electric still on"
              data-testid="add-vacant-notes"
              rows={2}
              className="w-full border-2 border-[#0f172a] px-2 py-1 text-sm"/>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="h-10 px-3 bg-white border-2 border-slate-900 hover:bg-slate-50 text-slate-900 font-black uppercase text-[10px] tracking-widest">
              Cancel
            </button>
            <button onClick={save} disabled={busy}
              data-testid="add-vacant-save-btn"
              className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-fuchsia-300 text-white font-black uppercase text-[10px] tracking-widest shadow-[2px_2px_0_#0f172a] border-2 border-[#0f172a]">
              {busy ? "Saving…" : (isEdit ? "Save Changes" : "Save Vacant Unit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Compact one-line risk row — big risk badges on left, 3 inline-action
 * buttons on the right. Designed to live inside the Squatter Risk Hotlist.
 */
function RiskRow({ r, readOnly, busy, onDispatch, onAlarm, onSecured }) {
  const addr = (r.address || r.address_key || "—");
  const scoreCls =
    r.risk_score >= 90 ? "bg-red-600 text-white" :
    r.risk_score >= 70 ? "bg-red-500 text-white" :
    r.risk_score >= 40 ? "bg-amber-500 text-white" : "bg-slate-300 text-slate-800";
  const busyDispatch = busy === r.id + ":dispatch";
  const busySecure   = busy === r.id + ":secure";

  return (
    <div className="flex items-center gap-2 p-2 bg-white border-2 border-slate-900 shadow-[2px_2px_0_#000]"
         data-testid={`risk-row-${r.id}`}>
      <div className={`h-10 w-10 flex-shrink-0 flex flex-col items-center justify-center font-black ${scoreCls}`}
           title={`Risk score ${r.risk_score}/100`}>
        <div className="text-[9px] leading-none opacity-80">RISK</div>
        <div className="text-[13px] leading-none tabular-nums">{r.risk_score}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-black text-[13px] text-slate-900 truncate">{addr}</span>
          {r.unit && <span className="text-[11px] text-slate-500 font-bold">· Unit {r.unit}</span>}
        </div>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          {r.flags?.no_alarm && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-red-200 text-red-800 px-1 py-0.5">🚨 No Alarm</span>
          )}
          {!r.flags?.no_alarm && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1 py-0.5">🔒 {r.alarm_code || "Armed"}</span>
          )}
          {r.flags?.stale_visit && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-amber-200 text-amber-900 px-1 py-0.5">
              🏃 {r.days_since_visit == null ? "never visited" : `${r.days_since_visit}d since visit`}
            </span>
          )}
          {r.flags?.long_vacant && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-slate-800 text-white px-1 py-0.5">📅 {r.days_vacant}d vacant</span>
          )}
          {!r.flags?.long_vacant && r.days_vacant != null && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-1 py-0.5">📅 {r.days_vacant}d</span>
          )}
        </div>
      </div>
      {!readOnly && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onDispatch} disabled={busyDispatch}
            data-testid={`risk-dispatch-${r.id}`}
            title="Dispatch a drive-by security check WO"
            className="h-9 px-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1">
            <Truck weight="fill" size={12}/>
            <span className="hidden sm:inline">{busyDispatch ? "…" : "Dispatch"}</span>
          </button>
          {r.flags?.no_alarm && (
            <button onClick={onAlarm}
              data-testid={`risk-alarm-${r.id}`}
              title="Add a Ring alarm to this unit"
              className="h-9 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1">
              <Siren weight="fill" size={12}/>
              <span className="hidden sm:inline">+ Alarm</span>
            </button>
          )}
          <button onClick={onSecured} disabled={busySecure}
            data-testid={`risk-secured-${r.id}`}
            title="Stamp 'I drove by, it's fine' — resets stale-visit clock"
            className="h-9 px-2.5 bg-white border-2 border-slate-900 hover:bg-emerald-50 text-slate-900 font-black uppercase text-[10px] tracking-widest flex items-center gap-1">
            <CheckCircle weight="fill" size={12}/>
            <span className="hidden sm:inline">{busySecure ? "…" : "Secured"}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function VacantSecurity() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [readOnly, setReadOnly] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [logFor, setLogFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [sendingAddendum, setSendingAddendum] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editVacancy, setEditVacancy] = useState(null);
  // Risk-dashboard overlay — same tenant, same property set, but with
  // computed risk flags (no_alarm / stale_visit / long_vacant) + score.
  const [risk, setRisk] = useState(null);
  const [riskCounts, setRiskCounts] = useState({});
  const [riskFilter, setRiskFilter] = useState("high_risk");  // high_risk | no_alarm | stale_visit | long_vacant | all
  const [actionBusyId, setActionBusyId] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/vacant-security"),
        api.get("/vacant-security/risk-dashboard"),
      ]);
      setRows(a.data.properties || []);
      setCounts(a.data.counts || {});
      setReadOnly(!!a.data.read_only);
      setRisk(b.data.rows || []);
      setRiskCounts(b.data.counts || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load vacant units");
      setRows([]);
      setRisk([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows || [];
    if (filter !== "all") out = out.filter(r => r.vacancy_status === filter);
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter(r =>
        (r.address_key || "").toLowerCase().includes(n) ||
        (r.address || "").toLowerCase().includes(n) ||
        (r.unit || "").toLowerCase().includes(n),
      );
    }
    return out;
  }, [rows, filter, q]);

  const createProject = async (p) => {
    if (!window.confirm(`Auto-create a Ring install ProjectRadar for ${p.address_key} unit ${p.unit}?`)) return;
    try {
      const r = await api.post("/projects/ring-alarm-install", { property_id: p.id });
      toast.success(`Created install project with ${r.data.punch_count} steps`);
      await load();
      nav(`/admin/projects/${r.data.project_id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Couldn't create project"); }
  };

  const flipStatus = async (p) => {
    const next = p.vacancy_status === "vacant_alarmed" ? "vacant_unsecured" : "vacant_alarmed";
    try {
      await api.patch(`/property-configs/${p.id}/security-status`, { vacancy_status: next });
      toast.success(next === "vacant_alarmed" ? "Marked alarmed" : "Marked unsecured");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Couldn't flip status"); }
  };

  const sendAddendum = async (p) => {
    const landlordEmail = p.landlord_email || "the linked landlord";
    if (!window.confirm(
      `Email the alarm addendum PDF to ${landlordEmail} for ${p.address}${p.unit ? ' · Unit ' + p.unit : ''}?\n\n` +
      `Covers: install charges, monthly monitoring, liability, removal-on-vacate.\n` +
      `Silent — only the landlord is contacted. Tenant is never notified.`
    )) return;
    setSendingAddendum(p.id);
    try {
      const r = await api.post(`/property-configs/${p.id}/alarm-addendum/send`);
      toast.success(
        r.data?.delivered
          ? `✅ Addendum emailed to ${r.data.to}`
          : `Addendum queued (email delivery pending) → ${r.data.to}`,
        { description: r.data?.landlord_name || "" }
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send addendum");
    } finally {
      setSendingAddendum("");    }
  };

  // Dispatch a drive-by security check WO. One tap — lands in the Pool
  // so the next tech can grab it. Flashes a toast with the WO title.
  const dispatchDriveBy = async (r) => {
    if (!window.confirm(
      `Dispatch a drive-by security check at ${r.address}${r.unit ? " · Unit " + r.unit : ""}?\n\n` +
      `This creates a WO in the Pool so the next tech can swing by, confirm the unit is still secure, ` +
      `and photograph the door + windows.`
    )) return;
    setActionBusyId(r.id + ":dispatch");
    try {
      const resp = await api.post(`/vacant-security/${r.id}/dispatch-drive-by`);
      toast.success(`🚨 Drive-by dispatched`, { description: resp.data?.title || "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't dispatch");
    } finally { setActionBusyId(""); }
  };

  // Office-level "I just drove by, it's fine" stamp — resets the stale
  // visit clock without creating a formal WO.
  const markSecured = async (r) => {
    const note = window.prompt(
      `Mark ${r.address}${r.unit ? " · Unit " + r.unit : ""} as secured today?\n\n` +
      `Optional note (e.g. "drove by on lunch, locks + windows intact")`,
      "",
    );
    if (note === null) return;
    setActionBusyId(r.id + ":secure");
    try {
      await api.post(`/vacant-security/${r.id}/mark-secured`, { note });
      toast.success(`✓ Marked secured`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't stamp secured");
    } finally { setActionBusyId(""); }
  };

  // "+ Alarm" — open the existing AlarmDetailsModal pre-pointed at this
  // property so the office can fill in a Ring site code / sensors right
  // from the risk list.
  const addAlarmFor = (r) => {
    const match = (rows || []).find(p => p.id === r.id);
    setEditFor(match || {
      id: r.id, address: r.address, address_key: r.address_key, unit: r.unit,
      ring_alarm: {}, has_ring_alarm: false,
    });
  };

  // Filtered risk list — defaults to showing high-risk (score ≥ 70) but
  // lets the office pivot to any individual signal.
  const riskVisible = useMemo(() => {
    const list = risk || [];
    if (!list.length) return [];
    if (riskFilter === "all")         return list;
    if (riskFilter === "high_risk")   return list.filter(r => r.risk_score >= 70);
    if (riskFilter === "no_alarm")    return list.filter(r => r.flags?.no_alarm);
    if (riskFilter === "stale_visit") return list.filter(r => r.flags?.stale_visit);
    if (riskFilter === "long_vacant") return list.filter(r => r.flags?.long_vacant);
    return list;
  }, [risk, riskFilter]);

  if (rows === null) {
    return <AdminShell><div className="p-8 text-center font-black uppercase text-slate-400">Loading…</div></AdminShell>;
  }

  return (
    <AdminShell>
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="vacant-security-page">
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ShieldWarning weight="fill" size={28} className="text-red-600"/>
              <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">Vacant Security</h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Every empty door in the portfolio — armed status, last check, event log. Squatter proofing.
              {readOnly && <span className="ml-2 px-2 py-0.5 bg-slate-200 text-slate-700 text-[10px] font-black uppercase tracking-widest">Read-only</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)} placeholder="Search address / unit"
              data-testid="vacant-search"
              className="h-10 w-56 border-2 border-[#0f172a] px-2 text-sm"/>
            {!readOnly && (
              <>
                <button onClick={() => setAddOpen(true)} data-testid="vacant-add-single-btn"
                  className="h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] shadow-[2px_2px_0_#0f172a]">
                  + Add Vacant
                </button>
                <button onClick={() => setBulkOpen(true)} data-testid="vacant-bulk-add-btn"
                  className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                  + Bulk Add
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <button onClick={() => setFilter("all")} data-testid="filter-all"
            className={`p-3 border-2 border-[#0f172a] text-left ${filter === "all" ? "bg-[#0f172a] text-white" : "bg-white text-slate-800 hover:bg-slate-50"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">All Vacant</div>
            <div className="font-display font-black text-2xl">{counts.total || 0}</div>
          </button>
          <button onClick={() => setFilter("vacant_unsecured")} data-testid="filter-unsecured"
            className={`p-3 border-2 border-[#0f172a] text-left ${filter === "vacant_unsecured" ? "bg-red-600 text-white" : "bg-red-50 text-red-800 hover:bg-red-100"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest">⚠️ Unsecured</div>
            <div className="font-display font-black text-2xl">{counts.vacant_unsecured || 0}</div>
          </button>
          <button onClick={() => setFilter("vacant_alarmed")} data-testid="filter-alarmed"
            className={`p-3 border-2 border-[#0f172a] text-left ${filter === "vacant_alarmed" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest">🔒 Alarmed</div>
            <div className="font-display font-black text-2xl">{counts.vacant_alarmed || 0}</div>
          </button>
        </div>

        {/* 🔥 SQUATTER RISK HOTLIST — vacant doors ranked by exposure.
            Signals: no alarm (+40), no tech visit in 14d (+30), vacant
            > 30d (+30). Office can one-tap dispatch a drive-by, slap on
            an alarm, or stamp "I drove by, it's fine" right from here. */}
        {(risk || []).length > 0 && (
          <section className="mb-4 bg-gradient-to-br from-red-50 to-amber-50 border-2 border-red-600 p-3" data-testid="risk-hotlist">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <Fire weight="fill" size={20} className="text-red-600"/>
                  <div className="font-display font-black text-base uppercase tracking-tight">Squatter Risk Hotlist</div>
                </div>
                <div className="text-[10px] text-slate-600 uppercase tracking-widest mt-0.5">
                  Highest exposure first — alarm, visit, days-vacant
                </div>
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                {[
                  { key: "high_risk",   label: `🔥 High`,     n: riskCounts.high_risk    || 0, color: "red" },
                  { key: "no_alarm",    label: `🚨 No alarm`, n: riskCounts.no_alarm     || 0, color: "orange" },
                  { key: "stale_visit", label: `🏃 No visit`, n: riskCounts.stale_visit  || 0, color: "amber" },
                  { key: "long_vacant", label: `📅 >30d`,     n: riskCounts.long_vacant  || 0, color: "slate" },
                  { key: "all",         label: `All`,         n: riskCounts.total        || 0, color: "slate" },
                ].map(c => (
                  <button key={c.key} onClick={() => setRiskFilter(c.key)}
                    data-testid={`risk-filter-${c.key}`}
                    className={`h-7 px-2 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest ${
                      riskFilter === c.key ? "bg-[#0f172a] text-white" : "bg-white text-slate-800 hover:bg-slate-50"
                    }`}>
                    {c.label} · {c.n}
                  </button>
                ))}
              </div>
            </div>
            {riskVisible.length === 0 ? (
              <div className="text-center py-4 text-[11px] text-slate-500 italic">
                Nothing matches this risk slice — try another chip
              </div>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                {riskVisible.slice(0, 60).map(r => (
                  <RiskRow key={r.id} r={r} readOnly={readOnly}
                    busy={actionBusyId}
                    onDispatch={() => dispatchDriveBy(r)}
                    onAlarm={() => addAlarmFor(r)}
                    onSecured={() => markSecured(r)}/>
                ))}
                {riskVisible.length > 60 && (
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center pt-1">
                    + {riskVisible.length - 60} more hidden
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Vacant Timeline — horizontal bars per unit, sorted longest-first so
            the doors that have been empty the longest bubble to the top. Bars
            are red for unsecured and emerald for alarmed so you can see the
            "empty AND unarmed" outliers at a glance. */}
        {(rows || []).length > 0 && (
          <section className="mb-4 bg-white border-2 border-[#0f172a] p-3" data-testid="vacant-timeline">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
              <div className="font-display font-black text-sm uppercase tracking-tight">📅 Vacant Timeline</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sorted longest-first · 🔴 unsecured · 🟢 alarmed</div>
            </div>
            {(() => {
              const withDays = (rows || []).map(r => ({
                ...r, _days: daysSince(r.went_vacant_at) ?? 0,
              })).sort((a, b) => b._days - a._days);
              const maxDays = Math.max(1, ...withDays.map(r => r._days));
              return (
                <div className="space-y-1">
                  {withDays.slice(0, 24).map(r => {
                    const pct = Math.max(3, (r._days / maxDays) * 100);
                    const isUnsecured = r.vacancy_status === "vacant_unsecured";
                    return (
                      <div key={r.id} className="flex items-center gap-2" data-testid={`timeline-row-${r.id}`}>
                        <div className="w-40 text-[11px] font-black uppercase tracking-widest text-slate-700 truncate flex-shrink-0" title={`${r.address_key || ""} · Unit ${r.unit || ""}`}>
                          {(r.address_key || "—").split(" ").slice(0, 2).join(" ")} · {r.unit || "—"}
                        </div>
                        <div className="flex-1 h-5 bg-slate-100 relative">
                          <div className={`absolute inset-y-0 left-0 flex items-center justify-end px-1.5 ${isUnsecured ? "bg-red-600" : "bg-emerald-600"} text-white text-[10px] font-black`}
                            style={{ width: `${pct}%` }}>
                            {r._days}d
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {withDays.length > 24 && (
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center pt-1">
                      + {withDays.length - 24} more not shown
                    </div>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {filtered.length === 0 ? (
          <div className="p-10 bg-white border-2 border-dashed border-slate-300 text-center">
            <ShieldCheck weight="fill" size={40} className="mx-auto text-emerald-500 mb-2"/>
            <div className="font-display font-black text-lg uppercase">
              {counts.total === 0 ? "No vacant units — everyone's home" : "Nothing matches this filter"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {counts.total === 0
                ? "Flip a unit's status from the Properties page to bring it into this workflow."
                : "Try a different filter or clear the search box."}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(p => (
              <PropertyCard key={p.id} p={p} readOnly={readOnly}
                onLog={setLogFor} onEdit={setEditFor} onEditVacancy={setEditVacancy}
                onCreateProject={createProject} onFlip={flipStatus}
                onSendAddendum={sendAddendum} sendingAddendum={sendingAddendum}/>
            ))}
          </div>
        )}
      </div>

      {logFor && <LogCheckModal property={logFor} onClose={() => setLogFor(null)} onLogged={load}/>}
      {editFor && <AlarmDetailsModal property={editFor} onClose={() => setEditFor(null)} onSaved={load}/>}
      {bulkOpen && <BulkAddAlarmsModal onClose={() => setBulkOpen(false)} onSaved={load}/>}
      {addOpen && <AddVacantModal onClose={() => setAddOpen(false)} onSaved={load}/>}
      {editVacancy && (
        <AddVacantModal
          editing={{
            address: editVacancy.address,
            unit: editVacancy.unit,
            vacancy_status: editVacancy.vacancy_status,
            went_vacant_at: editVacancy.went_vacant_at,
            vacancy_notes: editVacancy.vacancy_notes,
            alarm_code: (editVacancy.ring_alarm?.site_code) || "",
          }}
          onClose={() => setEditVacancy(null)}
          onSaved={load}/>
      )}
    </AdminShell>
  );
}
